import React from 'react';
import {
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  ButtonText,
  ScrollView,
  Icon,
  Pressable,
  Box,
} from '@gluestack-ui/themed';
import { ArrowRight, X } from 'lucide-react-native';
import Animated, { useAnimatedStyle, interpolate } from 'react-native-reanimated';
import type { SharedValue } from 'react-native-reanimated';
import { useTranslation } from 'react-i18next';

import { Module, Task } from '@/student/types';
import { OralExamFactsSummary } from '@/common/components/OralExamFactsSummary';
import { isEventDeliveredTask, isMeetingOnlyTask } from '@/common/utils/tasks/isEventDeliveredTask';
import { isOralExamPracticeTask } from '@/common/utils/oralExam/practiceApi';
import { isOralExamTaken } from '@/common/utils/oralExam/examTaken';
import TaskDescription from '@/student/task/components/Instructions/TaskDescription';
import { TaskStatus } from './TaskStatus';
import TaskObjectives, { hasApplicableObjectives } from '../../task/components/Instructions/TaskObjectives';
import { EventBookingSection } from './EventBookingSection';
import { OralExamBeforeExamChecklist } from './task-types/event/components/OralExamBeforeExamChecklist';
import { TaskRequirements } from './TaskRequirements';
import { useEventTaskSlots } from '../hooks/useEventTaskSlots';

const AnimatedBox = Animated.createAnimatedComponent(Box);

const DRAWER_RADIUS = 16;
const DRAWER_TOP_BOTTOM = 16;
const DRAWER_Z_INDEX = 1000;

interface TaskDetailDrawerProps {
  task: Task | null;
  tasks: Task[];
  module: Module;
  selectedModule: { courseId: string; moduleId: string };
  isMobile: boolean;
  drawerWidth: SharedValue<number>;
  drawerTargetWidth: number;
  onClose: () => void;
  onOpenTask: (task: Task) => void;
}

/**
 * TaskDetailDrawer
 * ----------------
 * Slide-in drawer showing full task details.
 *
 * Logic follows the "widened interpretation" of task state:
 * - For event tasks, progress is apparent through the session booking interface.
 *   The state indicates whether the user still wishes to participate or is done.
 * - For tasks with "hidden" work (like essays), we provide an explicit state
 *   summary and a navigation button to the dedicated task screen.
 */
export const TaskDetailDrawer: React.FC<TaskDetailDrawerProps> = ({
  task,
  tasks,
  module,
  selectedModule,
  isMobile,
  drawerWidth,
  drawerTargetWidth,
  onClose,
  onOpenTask,
}) => {
  const { t } = useTranslation();
  const animatedStyle = useAnimatedStyle(() => ({
    width: drawerTargetWidth,
    opacity: drawerWidth.value > 0 ? 1 : 0,
    transform: [{ translateX: interpolate(drawerWidth.value, [0, drawerTargetWidth], [drawerTargetWidth, 0]) }],
  }));

  const taskTitle = task?.title || 'Untitled Task';
  const taskType = task?.type || 'essay';
  // "Meeting-only" = task whose primary action lives inside EventBookingSection
  // (Join Room / Start Session) rather than navigating to a separate task
  // screen. Oral exam follows the same pattern as a generic event task.
  const meetingOnlyTask = isMeetingOnlyTask(task);
  const hasEventConfig = Boolean(task?.eventConfig);
  const isEventDeliveredMcq = taskType === 'multiple_choice' && hasEventConfig;
  const showEventBooking = isEventDeliveredTask(task);
  const courseId = selectedModule?.courseId || module?.courses?.[0]?.id;
  const moduleId = module?.id;
  const moduleVersion = module?.version;
  const { currentBooking } = useEventTaskSlots(courseId, moduleId, moduleVersion, task?.id || '', {
    enabled: Boolean(task) && showEventBooking,
    pollSlots: false,
    pollBookings: false,
    task,
  });
  if (!task) return null;

  const isOralExam = task.type === 'oral_exam';
  const isPracticeOralExam = isOralExamPracticeTask(task);
  const hasDescriptionContent = Boolean(task.description?.trim());
  const showDescriptionSection = isOralExam ? hasDescriptionContent : Boolean(task.description);
  const descriptionSectionLabelKey = isOralExam
    ? 'student.eventTask.description.additionalCommentsTitle'
    : 'student.task.instructions.description-tab';

  const canOpenTask = !isEventDeliveredMcq || Boolean(currentBooking);
  const isCompleted = task?.submission?.state === 'marked_as_done';
  const isGradedOralExam = isOralExam && !isPracticeOralExam;
  const isExamTaken = isOralExamTaken(task, currentBooking);
  const bookingSection = (
    <EventBookingSection
      task={task}
      module={module}
      selectedModule={selectedModule}
      onStartQuiz={() => onOpenTask(task)}
    />
  );
  const actionLabel = isCompleted
    ? taskType === 'multiple_choice'
      ? t('student.module.task-overview.button-opentask.redo-quiz', 'Redo Quiz')
      : t('student.module.task-overview.button-opentask.view-task', 'View Task')
    : t('student.module.task-detail.start-session', 'Start Session');

  return (
    <AnimatedBox
      testID="task-detail-drawer"
      position="absolute"
      top={isMobile ? 0 : DRAWER_TOP_BOTTOM}
      right={isMobile ? 0 : DRAWER_TOP_BOTTOM}
      bottom={isMobile ? 0 : DRAWER_TOP_BOTTOM}
      left={isMobile ? 0 : undefined}
      bg="$white"
      borderRadius={isMobile ? 0 : DRAWER_RADIUS}
      borderWidth={isMobile ? 0 : 1}
      borderColor="$borderLight"
      overflow="hidden"
      zIndex={DRAWER_Z_INDEX}
      softShadow="3"
      style={animatedStyle}
    >
      <VStack flex={1}>
        {/* Header */}
        <Box px="$4" py="$4" borderBottomWidth={1} borderBottomColor="$borderLight" bg="$bgApp">
          <HStack alignItems="flex-start" justifyContent="space-between">
            <VStack flex={1} mr="$3" space="xs">
              <Text
                size="2xs"
                fontWeight="bold"
                color="$brandBase"
                textTransform="uppercase"
                letterSpacing={1}
              >
                {taskType}
              </Text>
              <Heading size="md" color="$textHeading" numberOfLines={2}>
                {taskTitle}
              </Heading>

              {/* Compact Metadata in Header */}
              <HStack space="sm" alignItems="center" mt="$1">
                <TaskStatus task={task} variant="compact" />
              </HStack>
            </VStack>

            <Pressable onPress={onClose} hitSlop={10}>
              {({ pressed }) => (
                <Box
                  w={32}
                  h={32}
                  borderRadius="$full"
                  bg={pressed ? '$bgHover' : '$bgSubtle'}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Icon as={X} size="sm" color="$textSubtle" />
                </Box>
              )}
            </Pressable>
          </HStack>
        </Box>

        {/* Scrollable Content */}
        <ScrollView
          flex={1}
          sx={{
            _contentContainerStyle: {
              paddingBottom: 24,
            },
          }}
          showsVerticalScrollIndicator={false}
        >
          <VStack space="xl" px="$4" py="$6">
            {isOralExam && !isPracticeOralExam && (
              <OralExamFactsSummary
                resolvedQuestionCount={task.oralExamConfig?.resolvedQuestionCount}
                resolvedSecondsPerQuestion={task.oralExamConfig?.resolvedSecondsPerQuestion}
                poolSize={task.oralExamConfig?.questionPool?.length ?? 0}
                variant="compact"
              />
            )}

            {isGradedOralExam && !!showEventBooking && (
              <VStack space="sm" testID="oral-exam-registration-section">
                <Heading size="sm">{t('oralExam.registration.title')}</Heading>
                {bookingSection}
              </VStack>
            )}

            {isGradedOralExam && !isExamTaken && (
              <OralExamBeforeExamChecklist
                bookingStartIso={
                  currentBooking && currentBooking.status !== 'cancelled'
                    ? currentBooking.startTime
                    : null
                }
                questionCount={task.oralExamConfig?.questionPool?.length ?? 0}
                onOpenQuestions={() => onOpenTask(task)}
                dryRunTaskId={task.id}
              />
            )}

            {/* Task Description Section */}
            {showDescriptionSection && (
              <VStack space="md">
                <Heading size="sm">
                  {t(descriptionSectionLabelKey, isOralExam ? 'Additional comments' : 'Task')}
                </Heading>
                <TaskDescription description={task.description} variant="comfortable" />
              </VStack>
            )}

            {/* Objectives Section */}
            {hasApplicableObjectives(task.analysis?.dimensions) && (
              <VStack space="md">
                <Heading size="sm">
                  {t('student.task.instructions.objectives-tab', 'Objectives')}
                </Heading>
                <TaskObjectives objectives={task.analysis.dimensions} variant="comfortable" color="brandBase" />
              </VStack>
            )}

            {/* Event Booking Section for all other event-configured tasks */}
            {!!showEventBooking && !isGradedOralExam && bookingSection}

            {/* Task Requirements (The "Footnote") */}
            <TaskRequirements task={task} tasks={tasks} variant="comfortable" />
          </VStack>
        </ScrollView>

        {/* Bottom Action - Only for non-meeting tasks with required booking in place */}
        {!meetingOnlyTask && canOpenTask && (
          <Box px="$4" py="$4" borderTopWidth={1} borderTopColor="$borderLight" bg="$bgApp">
            <Button
              testID="open-task-button"
              size="lg"
              bg="$actionPrimary"
              borderRadius="$lg"
              h={52}
              onPress={() => onOpenTask(task)}
            >
              <HStack space="sm" alignItems="center">
                <ButtonText fontWeight="bold" fontSize="$md">
                  {actionLabel}
                </ButtonText>
                <Icon as={ArrowRight} size="sm" color="white" />
              </HStack>
            </Button>
          </Box>
        )}
        {!meetingOnlyTask && isEventDeliveredMcq && !canOpenTask && (
          <Box px="$4" py="$4" borderTopWidth={1} borderTopColor="$borderLight" bg="$bgApp">
            <Text size="sm" color="$textSubtle" textAlign="center">
              {t('student.module.task-detail.book-session-first')}
            </Text>
          </Box>
        )}
      </VStack>
    </AnimatedBox>
  );
};

export default TaskDetailDrawer;
